import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ConsoleIdentity } from '@/lib/console-auth.ts';

/**
 * The console's credential, held in React state and nowhere else.
 *
 * **Never `localStorage`, `sessionStorage`, or IndexedDB** (§0.2 rule 4). Fragments in the trusted
 * tier share the origin, so anything in web storage is readable by every one of them; a token
 * there is a token every fragment on every tenant page can lift. Keeping it in a closure means it
 * dies with the tab, which is the correct lifetime for a credential.
 *
 * ### Two layers, because phase 3 separated them
 *
 * Signing in and choosing an org used to be the same act: the phase-2 form asked for an org id and
 * a token together. A console sign-in yields a person, who may belong to several orgs or to none,
 * and every route below `/api/orgs/{orgId}` needs one to be chosen. So:
 *
 * - **`credential`** — who is holding the console. Survives switching orgs.
 * - **`session`** — that credential *plus* the org it is currently pointed at. This is what every
 *   page consumes, and its shape has not changed, which is why none of them had to.
 *
 * An API token collapses the two: it names exactly one org, so signing in with one sets both at
 * once and `orgs` is a list of one.
 */
export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Credential {
  /** `console` is a Cognito ID token; `token` is an API token a human pasted in. */
  kind: 'console' | 'token';
  token: string;
  /** The signed-in person. Absent for an API token, which belongs to no one. */
  email?: string;
  /** Epoch milliseconds. Console sessions only — an API token does not expire on a clock. */
  expiresAt?: number;
}

/** One org, as `GET /api/orgs` returns it. */
export interface OrgSummary {
  orgId: string;
  slug: string;
  name: string;
  plan: string;
  role: Role;
}

export interface Session {
  orgId: string;
  token: string;
  /** Resolved at sign-in from `GET /me`, so the UI can show what this credential may do. */
  role: Role;
  orgSlug: string;
  orgName: string;
}

interface SessionValue {
  credential: Credential | null;
  session: Session | null;
  /** Records who is holding the console. Does not choose an org. */
  authenticate(credential: Credential): void;
  /** Points the current credential at one org. */
  selectOrg(org: OrgSummary): void;
  /** Back to the org picker, still signed in. */
  leaveOrg(): void;
  signIn(session: Session): void;
  signOut(): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [credential, setCredential] = useState<Credential | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  // Reads `credential` from the closure rather than from a `setCredential` updater: an updater
  // that also calls `setSession` is a side effect inside a reducer, which StrictMode runs twice.
  const selectOrg = useCallback((org: OrgSummary) => {
    if (!credential) throw new Error('selectOrg called with no credential');
    setSession({
      orgId: org.orgId,
      token: credential.token,
      role: org.role,
      orgSlug: org.slug,
      orgName: org.name,
    });
  }, [credential]);

  const value = useMemo<SessionValue>(() => ({
    credential,
    session,
    authenticate: (next) => setCredential(next),
    selectOrg,
    leaveOrg: () => setSession(null),
    signIn: (next) => {
      setCredential({ kind: 'token', token: next.token });
      setSession(next);
    },
    signOut: () => {
      setCredential(null);
      setSession(null);
    },
  }), [credential, session, selectOrg]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}

/** The signed-in session, for components that only render behind the sign-in gate. */
export function useAuthenticated(): Session {
  const { session } = useSession();
  if (!session) throw new Error('this component renders only when signed in');
  return session;
}

/** Whether this role may change anything. Mirrors the control plane's own policy. */
export function canWrite(role: Role): boolean {
  return role === 'owner' || role === 'admin' || role === 'editor';
}

export function canAdminister(role: Role): boolean {
  return role === 'owner' || role === 'admin';
}

/** A console identity, as the credential the rest of the app carries. */
export function credentialFor(identity: ConsoleIdentity): Credential {
  return { kind: 'console', token: identity.token, email: identity.email, expiresAt: identity.expiresAt };
}
